import React from 'react';
import { motion } from 'framer-motion';
import { FiGlobe, FiShield, FiAlertTriangle, FiCheckCircle, FiEye, FiActivity, FiTrendingUp, FiSettings, FiZap } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const DAST = () => {
  const currentStats = [
    { label: "Total Scans", value: "15", description: "DAST scans completed", icon: <FiGlobe /> },
    { label: "Applications Tested", value: "8", description: "web applications analyzed", icon: <FiActivity /> },
    { label: "Vulnerabilities Found", value: "23", description: "security issues identified", icon: <FiAlertTriangle /> },
    { label: "Last Scan", value: "3", description: "days ago", icon: <FiTrendingUp /> }
  ];

  const coreFeatures = [
    {
      title: "Dynamic Web Application Testing",
      icon: <FiGlobe />,
      features: [
        "Runtime Analysis: Testing applications in their running state",
        "Black-box Testing: External testing without access to source code",
        "Automated Crawling: Comprehensive discovery of application endpoints",
        "Interactive Testing: Simulation of real-world attack scenarios"
      ]
    },
    {
      title: "Vulnerability Detection",
      icon: <FiZap />,
      features: [
        "OWASP Top 10: Detection of OWASP Top 10 web vulnerabilities",
        "Injection Attacks: SQL, NoSQL, LDAP, and command injection testing",
        "Authentication Flaws: Broken authentication and session management",
        "Business Logic Flaws: Complex business logic vulnerability detection"
      ]
    },
    {
      title: "Security Assessment",
      icon: <FiShield />,
      features: [
        "Risk Assessment: Automated risk scoring and impact analysis",
        "Compliance Testing: Testing against security standards and regulations",
        "Penetration Testing: Automated penetration testing capabilities",
        "Security Posture: Overall security posture assessment"
      ]
    }
  ];

  const testingCapabilities = [
    {
      category: "Web Application Testing",
      capabilities: [
        "HTTP/HTTPS Protocol Testing: Comprehensive protocol-level testing",
        "REST API Testing: Automated testing of RESTful web services",
        "SOAP API Testing: Testing of SOAP-based web services",
        "GraphQL Testing: Security testing of GraphQL endpoints"
      ]
    },
    {
      category: "Authentication Testing",
      capabilities: [
        "Login Mechanism Testing: Testing of authentication workflows",
        "Session Management: Session handling and security testing",
        "Multi-factor Authentication: MFA implementation testing",
        "OAuth/SAML Testing: Testing of federated authentication systems"
      ]
    },
    {
      category: "Input Validation Testing",
      capabilities: [
        "SQL Injection Testing: Comprehensive SQL injection detection",
        "Cross-Site Scripting (XSS): XSS vulnerability identification",
        "Cross-Site Request Forgery (CSRF): CSRF protection testing",
        "File Upload Testing: Malicious file upload vulnerability testing"
      ]
    },
    {
      category: "Configuration Testing",
      capabilities: [
        "SSL/TLS Configuration: Encryption and certificate testing",
        "HTTP Security Headers: Security header configuration testing",
        "Directory Traversal: Path traversal vulnerability testing",
        "Information Disclosure: Sensitive information exposure testing"
      ]
    }
  ];

  const vulnerabilityCategories = [
    { category: "Critical Vulnerabilities", description: "Immediate security threats requiring urgent attention", color: "text-red-500" },
    { category: "High-Risk Issues", description: "Significant security vulnerabilities with high impact", color: "text-orange-500" },
    { category: "Medium-Risk Issues", description: "Moderate security issues requiring remediation", color: "text-yellow-500" },
    { category: "Low-Risk Issues", description: "Minor security concerns and informational findings", color: "text-blue-500" }
  ];

  const scanConfiguration = [
    {
      title: "Scan Types",
      options: [
        "Full Application Scan: Comprehensive testing of entire web application",
        "Targeted Scan: Focused testing of specific application components",
        "API-Only Scan: Dedicated testing of API endpoints and services",
        "Authenticated Scan: Testing with valid user credentials and sessions"
      ]
    },
    {
      title: "Testing Depth",
      options: [
        "Surface Scan: Quick security assessment for common vulnerabilities",
        "Deep Scan: Comprehensive security testing with advanced techniques",
        "Custom Scan: Tailored testing based on specific security requirements",
        "Compliance Scan: Testing focused on regulatory compliance standards"
      ]
    },
    {
      title: "Attack Simulation",
      options: [
        "Passive Scanning: Non-intrusive vulnerability identification",
        "Active Scanning: Controlled exploitation of identified vulnerabilities",
        "Aggressive Testing: Comprehensive penetration testing simulation",
        "Safe Mode: Conservative testing to minimize application impact"
      ]
    }
  ];

  const integrationCapabilities = [
    {
      title: "Development Workflow Integration",
      integrations: [
        "CI/CD Pipeline: Automated security testing in deployment pipelines",
        "Staging Environment: Pre-production security testing automation",
        "API Integration: RESTful APIs for custom integrations and automation",
        "Webhook Support: Real-time notifications and result delivery"
      ]
    },
    {
      title: "Security Tool Integration",
      integrations: [
        "SIEM Integration: Security event correlation and monitoring",
        "Vulnerability Management: Integration with vulnerability tracking platforms",
        "Issue Tracking: Automatic issue creation in Jira, GitHub, and other systems",
        "Reporting Tools: Integration with security reporting and analytics platforms"
      ]
    },
    {
      title: "Testing Environment Integration",
      integrations: [
        "Load Balancer Support: Testing behind load balancers and proxies",
        "WAF Integration: Testing with Web Application Firewall configurations",
        "CDN Testing: Content Delivery Network security assessment",
        "Cloud Platform: Integration with AWS, Azure, and GCP environments"
      ]
    }
  ];

  const analysisResults = [
    {
      category: "Vulnerability Assessment (23 Found)",
      items: [
        "Security Vulnerabilities: Identified security flaws and weaknesses",
        "Configuration Issues: Misconfigurations and security gaps",
        "Compliance Violations: Regulatory compliance violations",
        "Best Practice Deviations: Security best practice violations"
      ]
    },
    {
      category: "Application Coverage",
      items: [
        "Endpoint Discovery: Comprehensive mapping of application endpoints",
        "Functionality Testing: Testing of application business logic",
        "User Journey Analysis: Security testing of user workflows",
        "API Coverage: Complete API endpoint security assessment"
      ]
    },
    {
      category: "Risk Assessment",
      items: [
        "Impact Analysis: Assessment of potential security impact",
        "Exploitability Assessment: Evaluation of vulnerability exploitability",
        "Business Risk: Business impact assessment of identified vulnerabilities",
        "Remediation Priority: Risk-based remediation prioritization"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "DAST Implementation",
      practices: [
        "Regular Testing: Implement regular and automated security testing",
        "Environment Preparation: Ensure proper test environment configuration",
        "Authentication Setup: Configure proper authentication for comprehensive testing",
        "Scope Definition: Clearly define testing scope and boundaries",
        "Performance Monitoring: Monitor application performance during testing"
      ]
    },
    {
      category: "Vulnerability Management",
      practices: [
        "Risk Prioritization: Prioritize vulnerabilities based on risk and exploitability",
        "Rapid Response: Establish procedures for critical vulnerability response",
        "Verification Testing: Verify fixes through re-testing and validation",
        "Documentation: Maintain comprehensive vulnerability documentation",
        "Stakeholder Communication: Communicate findings to relevant stakeholders"
      ]
    },
    {
      category: "Testing Optimization",
      practices: [
        "Scan Scheduling: Schedule scans during low-traffic periods",
        "False Positive Management: Implement effective false positive handling",
        "Custom Payloads: Develop custom attack payloads for specific applications",
        "Performance Tuning: Optimize scan performance and resource usage",
        "Continuous Improvement: Continuously improve testing coverage and accuracy"
      ]
    }
  ];

  const advancedFeatures = [
    {
      title: "Intelligent Testing",
      features: [
        "Machine Learning: ML-powered vulnerability detection and classification",
        "Behavioral Analysis: Analysis of application behavior and anomalies",
        "Smart Crawling: Intelligent application discovery and mapping",
        "Adaptive Testing: Dynamic test adaptation based on application responses"
      ]
    },
    {
      title: "Advanced Attack Simulation",
      features: [
        "Multi-step Attacks: Complex multi-stage attack simulation",
        "Business Logic Testing: Advanced business logic vulnerability detection",
        "Race Condition Testing: Concurrency and timing attack simulation",
        "State Management: Stateful testing and session management"
      ]
    },
    {
      title: "Reporting and Analytics",
      features: [
        "Executive Dashboards: High-level security assessment dashboards",
        "Technical Reports: Detailed technical vulnerability reports",
        "Trend Analysis: Historical security trend analysis and metrics",
        "Custom Reporting: Customizable reporting templates and formats"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Dynamic Application Security Testing (DAST)" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The DAST module in Ana Security provides comprehensive dynamic security testing capabilities to identify vulnerabilities in running web applications and APIs through automated black-box testing and attack simulation.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/10_dast.png" 
                  alt="DAST Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              DAST Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new DAST analyses</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Application Filtering:</strong> Filter scans by specific applications</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Scan History:</strong> Complete history of all DAST scanning activities</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Real-time Monitoring:</strong> Live monitoring of ongoing scans</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              DAST Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Testing Capabilities */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Testing Capabilities
            </h2>
            
            <div className="space-y-8">
              {testingCapabilities.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.capabilities.map((capability, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiZap className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{capability}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Vulnerability Categories */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Vulnerability Categories (23 Found)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {vulnerabilityCategories.map((category, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className={`font-semibold mb-2 ${category.color}`}>
                    {category.category}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Common DAST Findings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Injection Vulnerabilities:</strong> SQL, NoSQL, LDAP, and command injection</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Cross-Site Scripting:</strong> Reflected, stored, and DOM-based XSS</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Authentication Issues:</strong> Broken authentication and session management</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Configuration Issues:</strong> Security misconfigurations and exposures</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Scan Configuration */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Scan Configuration Options
            </h2>
            
            <div className="space-y-8">
              {scanConfiguration.map((config, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {config.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {config.options.map((option, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{option}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Analysis Results */}
        <MotionDiv delay={0.6}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              DAST Analysis Results
            </h2>
            
            <div className="space-y-8">
              {analysisResults.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiEye className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Integration Capabilities */}
        <MotionDiv delay={0.7}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Integration Capabilities
            </h2>
            
            <div className="space-y-8">
              {integrationCapabilities.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.integrations.map((integration, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{integration}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Advanced Features */}
        <MotionDiv delay={0.8}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Advanced Features
            </h2>
            
            <div className="space-y-8">
              {advancedFeatures.map((feature, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiShield className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.9}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              DAST Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={1.0}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Test Your Running Applications?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using DAST to identify runtime vulnerabilities in your web applications and APIs.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/ana-actions" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Automatically fix runtime vulnerabilities</p>
              </a>
              
              <a 
                href="/dashboard" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your application security</p>
              </a>
              
              <a 
                href="/integrations" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your testing environment</p>
              </a>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default DAST;
